// Signing in, including the workspace picker.
//
// Nearly everyone types their email and password and lands in the app. The
// picker only appears for the handful of people whose address is on more than
// one company's account — a subcontractor working for two of our customers, for
// instance. They are asked which one, not made to remember a company code.
import { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api, type Workspace } from '../api';
import { useSession } from '../session';
import { AuthCard, Banner, Field } from '../ui';

export function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  // Which factors can answer it. Defaults to the code, which is what every
  // account had before round 32 and what a server that says nothing means.
  const [factors, setFactors] = useState({ totp: true, passkey: false });
  const [passkeyOptions, setPasskeyOptions] =
    useState<PublicKeyCredentialRequestOptionsJSON | null>(null);
  const [code, setCode] = useState('');
  // People who have lost the phone type a recovery code instead. Hidden behind
  // a link rather than shown as a second box: on the ordinary day it is one
  // more thing to read past on a screen where somebody is already holding
  // their phone.
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function attempt(tenantId?: string) {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.login(
        tenantId ? { email, password, tenantId } : { email, password }
      );
      if (result.result === 'workspace_required') {
        setWorkspaces(result.workspaces);
        return;
      }
      if (result.result === 'totp_required') {
        setWorkspaces(null);
        setChallenge(result.challenge);
        setFactors(result.factors ?? { totp: true, passkey: false });
        setPasskeyOptions(result.passkeyOptions ?? null);
        return;
      }
      await refresh();
      navigate('/app', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Something went wrong. Try again.');
      }
      setWorkspaces(null);
    } finally {
      setBusy(false);
    }
  }

  /**
   * The passkey half of the challenge (round 32).
   *
   * The options came down with the challenge itself rather than from a second
   * request, so there is nothing to fetch here — just the browser prompt and
   * the answer. A cancelled prompt is not an error and says nothing; the code
   * form is still on screen underneath.
   */
  async function answerWithPasskey() {
    if (!challenge || !passkeyOptions) return;
    setBusy(true);
    setError(null);
    try {
      const response = await startAuthentication({ optionsJSON: passkeyOptions });
      await api.passkeyLogin({ challenge, response });
      await refresh();
      navigate('/app', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        // Single-use, like the code path: a spent challenge means starting
        // from the password again, and saying so beats a form that has quietly
        // stopped working.
        if (err.status === 401) setChallenge(null);
      } else if (err instanceof Error && err.name !== 'NotAllowedError' && err.name !== 'AbortError') {
        setError('That did not work. Use your authenticator app instead.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function answerChallenge() {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await api.answerTotpChallenge(
        usingRecoveryCode ? { challenge, recoveryCode: code } : { challenge, code }
      );
      await refresh();
      navigate('/app', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Something went wrong. Try again.'
      );
      setCode('');
      // A challenge is single-use, so a wrong code means starting from the
      // password again. Saying so beats a form that silently stops working.
      if (err instanceof ApiError && err.status === 401 && /timed out/.test(err.message)) {
        setChallenge(null);
      }
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <AuthCard title={factors.totp ? 'Enter your code' : 'Use your passkey'}>
        {error && <Banner kind="error">{error}</Banner>}

        {/* Offered FIRST when the account has one: it is the factor that takes
            one tap rather than six digits typed off a second screen. */}
        {factors.passkey && (
          <>
            <button
              className="btn-primary full"
              type="button"
              disabled={busy}
              onClick={() => void answerWithPasskey()}
            >
              {busy ? 'Waiting for your device…' : 'Use a passkey'}
            </button>
            {factors.totp && <p className="footnote">or</p>}
          </>
        )}

        {!factors.totp && (
          <p className="footnote">
            <Link to="/lost-2fa">Lost the device with your passkey?</Link>
          </p>
        )}

        {factors.totp && (
        <>
        <p className="mb-4 text-sm text-gray-500">
          {usingRecoveryCode
            ? 'Type one of the recovery codes you saved when you set this up. Each one works once.'
            : 'Open your authenticator app and type the six-digit code it is showing.'}
        </p>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void answerChallenge();
          }}
        >
          <Field
            label={usingRecoveryCode ? 'Recovery code' : 'Six-digit code'}
            name="code"
            value={code}
            onChange={setCode}
            error={fields.code}
            autoComplete={usingRecoveryCode ? 'off' : 'one-time-code'}
            // A numeric keypad for the six digits, and a normal keyboard for a
            // recovery code, which has letters in it.
            inputMode={usingRecoveryCode ? 'text' : 'numeric'}
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="go"
          />
          <button className="btn-primary full" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
        </form>
        <p className="footnote">
          <button
            className="link"
            onClick={() => {
              setUsingRecoveryCode(!usingRecoveryCode);
              setCode('');
              setError(null);
            }}
          >
            {usingRecoveryCode
              ? 'Use my authenticator app instead'
              : 'I have lost my phone, use a recovery code'}
          </button>
        </p>
        <p className="footnote">
          <Link to="/lost-2fa">Lost your phone and your recovery codes?</Link>
        </p>
        </>
        )}
      </AuthCard>
    );
  }

  if (workspaces) {
    return (
      <AuthCard title="Which company?">
        <p className="mb-4 text-sm text-gray-500">
          That email address is on more than one Dayboard account. Choose the one you want.
        </p>
        {error && <Banner kind="error">{error}</Banner>}
        {workspaces.map((workspace) => (
          <button
            key={workspace.tenantId}
            className="full secondary"
            style={{ marginBottom: 10 }}
            disabled={busy}
            onClick={() => void attempt(workspace.tenantId)}
          >
            {workspace.tenantName}
          </button>
        ))}
        <p className="footnote">
          <button className="link" onClick={() => setWorkspaces(null)}>
            Use a different email address
          </button>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Sign in">
      {error && <Banner kind="error">{error}</Banner>}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void attempt();
        }}
      >
        <Field
          label="Email"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          error={fields.email}
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          value={password}
          onChange={setPassword}
          error={fields.password}
          autoComplete="current-password"
          enterKeyHint="go"
        />
        <button className="btn-primary full" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="footnote">
        <Link to="/forgot-password">Forgotten your password?</Link>
      </p>
      <p className="footnote">
        New to Dayboard? <Link to="/signup">Start a trial</Link>
      </p>
    </AuthCard>
  );
}
